import {
  Badge,
  Card,
  Flex,
  Modal,
  Select,
  type SelectProps,
  Table,
  type TableColumnsType,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import {
  Store,
  ClipboardClock,
  ClipboardCheck,
  Hourglass,
  CheckCircle,
  CircleX,
} from 'lucide-react';
import { type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { catchError, EMPTY, finalize, of, switchMap, tap, throwError } from 'rxjs';

import AuthorizedComponent from '../../../components/authorized-component';
import DetailedSite from '../../../components/detailted-site';
import LoadingSpin from '../../../components/loading-spin';
import SyncSuspense from '../../../components/sync-suspense';
import { AuthContext } from '../../../contexts/auth-context-provider';
import ProblemDetailsDto from '../../../dto/error/problem-details-dto';
import {
  ArchivedCustomerOrder,
  type CustomerOrder,
  type ScheduledMeal,
  type SiteMenuOrder,
} from '../../../entities';
import { type SiteMenuBeverageOrderView } from '../../../entities/site-menu-beverage-order';
import { type SiteMenuOrderView, toSiteMenuOrderView } from '../../../entities/site-menu-order';
import { type SiteMenuSupplyOrderView } from '../../../entities/site-menu-supply-order';

import { useNotification } from '../../../hooks/use-notification';
import useOrderSiteMenuOrders from '../../../hooks/use-order-site-menu-orders';
import useSecuredApiV2 from '../../../hooks/use-secured-api-v2';
import { OrderStatus, Roles } from '../../../types';
import { CUSTOMER_ORDER_END_POINT_URL } from '../../../utils/endpoints';
import { LocalDate } from '../../../utils/local-date';
import { totalMeals } from '../../../utils/order-utils';
import {
  isRouteSubtotal,
  type RouteSubtotalRow,
  routeSubtotalRowClassName,
  withRouteSubtotalCells,
  withRouteSubtotals,
} from '../../../utils/order-utils';
import utils, {
  sortSiteMenuOrdersByCountyAndName,
  sortSiteMenuOrdersByRouteThenMealCategorySortOrder,
} from '../../../utils/utils';
import type CustomerOrderRevision from '../../../entities/customer-order-revision';

interface OrderViewProps {
  revisionNumber?: CustomerOrderRevision['id'];
  customerOrder: CustomerOrder;
  onCustomerOrdersChange: () => void;
  onDismiss: () => void;
}
export const orderStatuses: Exclude<SelectProps['options'], undefined> = [
  {
    label: (
      <Flex gap="small" align="center">
        <ClipboardClock />
        Submitted
      </Flex>
    ),
    value: OrderStatus.Submitted,
  },
  {
    label: (
      <Flex gap="small" align="center">
        <ClipboardCheck />
        Accepted
      </Flex>
    ),
    value: OrderStatus.Accepted,
  },
  {
    label: (
      <Flex gap="small" align="center">
        <Hourglass />
        In Preparation
      </Flex>
    ),
    value: OrderStatus.InPreparation,
  },
  {
    label: (
      <Flex gap="small" align="center">
        <CheckCircle />
        Completed
      </Flex>
    ),
    value: OrderStatus.Completed,
  },
  {
    label: (
      <Flex gap="small" align="center">
        <CircleX />
        Canceled by Admin
      </Flex>
    ),
    value: OrderStatus.CanceledByAdmin,
  },
  {
    label: (
      <Flex gap="small" align="center">
        <CircleX />
        Canceled by Customer
      </Flex>
    ),
    value: OrderStatus.CanceledByCustomer,
  },
];
const pricesColumns = [
  {
    title: 'Total Price',
    dataIndex: 'totalPrice',
    width: 100,
    render: (value: number) => (
      <Typography.Text strong>{utils.currencyFormat(value)}</Typography.Text>
    ),
  },
];
const MEALS_LABEL_COLUMN_INDEX = 0;
const MEALS_QUANTITY_COLUMN_INDEX = 5;
const MEALS_TOTAL_PRICE_COLUMN_INDEX = 7;

const routeSubtotalAmounts = {
  quantity: (row: SiteMenuOrderView) => row.quantity ?? 0,
  totalPrice: (row: SiteMenuOrderView) =>
    row.totalPrice +
    (row.siteMenuBeverageOrders ?? []).reduce((sum, bev) => sum + bev.unitPrice * bev.quantity, 0) +
    (row.siteMenuSupplyOrders ?? []).reduce((sum, sup) => sum + sup.unitPrice * sup.quantity, 0),
};

const routeSubtotalCells = {
  [MEALS_QUANTITY_COLUMN_INDEX]: 'quantity',
  [MEALS_TOTAL_PRICE_COLUMN_INDEX]: {
    amount: 'totalPrice',
    format: (value: number) => utils.currencyFormat(value),
  },
};

const mealsListColumns: TableColumnsType<SiteMenuOrderView> = [
  {
    title: 'Meal Category',
    dataIndex: ['mealCategoryName'],
    ellipsis: true,
    render: (mealCategoryName: string, record: SiteMenuOrderView) =>
      `${mealCategoryName} (${record.mainType})`,
  },
  {
    title: 'Meal',
    dataIndex: ['scheduledMeal'],
    ellipsis: true,
    render: (scheduledMeal: ScheduledMeal) => (
      <Tooltip
        styles={{ body: { padding: 0 } }}
        title={
          <Card
            styles={{
              header: { textAlign: 'left', paddingInline: 20 },
              body: {
                paddingInline: 20,
                textAlign: 'left',
              },
            }}
            variant="borderless"
            style={{ minWidth: 300, minHeight: '60%' }}
            size="small"
            title={
              <Typography.Text style={{ justifySelf: 'flex-start' }}>
                {scheduledMeal.name}
              </Typography.Text>
            }
          >
            <Flex vertical gap="small">
              {(scheduledMeal.mealItemsNames?.length ?? 0) > 0 ? (
                <Typography.Text>Each contains:</Typography.Text>
              ) : undefined}
              {scheduledMeal.mealItemsNames.map((mealItemName, idx) => (
                <Badge key={idx} color="var(--ant-color-primary)" text={mealItemName} />
              ))}
            </Flex>
          </Card>
        }
      >
        <Typography.Paragraph style={{ margin: 0 }} ellipsis>
          {scheduledMeal.name}
        </Typography.Paragraph>
      </Tooltip>
    ),
  },
  {
    title: 'Beverages Prepacked?',
    dataIndex: ['prepackBeverages'],
    width: 200,
    render: (value: boolean) => (value ? 'Yes' : 'No'),
  },
  {
    title: 'Route',
    dataIndex: ['routeName'],
    width: 100,
    render: (value) => value ?? 'N/A',
  },
  { title: 'Service Type', dataIndex: ['serviceType'], width: 100 },
  {
    title: 'Quantity',
    dataIndex: 'quantity',
    width: 100,
    render: (quantity: number, record: SiteMenuOrderView) => (
      <Tooltip
        styles={{ body: { padding: 0 } }}
        title={
          <Card
            styles={{
              header: { textAlign: 'left', paddingInline: 20 },
              body: {
                paddingInline: 20,
                textAlign: 'left',
              },
            }}
            variant="borderless"
            style={{ minWidth: 300, minHeight: '60%' }}
            size="small"
          >
            <Flex vertical gap="small">
              {(record.diets?.length ?? 0) > 0 ? (
                <Typography.Text strong>Diets:</Typography.Text>
              ) : (
                <Typography.Text strong>No diets</Typography.Text>
              )}
              {record.diets?.map((diet, idx) => (
                <Badge key={idx} color="var(--ant-color-primary)" text={diet} />
              ))}
              {(record.preferences?.length ?? 0) > 0 ? (
                <Typography.Text strong>Preferences:</Typography.Text>
              ) : (
                <Typography.Text strong>No Preferences</Typography.Text>
              )}
              {record.preferences?.map((preference, idx) => (
                <Badge key={idx} color="var(--ant-color-primary)" text={preference} />
              ))}
            </Flex>
          </Card>
        }
      >
        <Typography.Paragraph style={{ margin: 0 }}>{quantity}</Typography.Paragraph>
      </Tooltip>
    ),
  },
  {
    title: 'Units per meal',
    dataIndex: ['scheduledMeal', 'mealCategory', 'units'],
    render: (value, record) => (record.scheduledMeal?.mealCategory.isCompleteMeal ? value : 'N/A'),
    key: 'units',
    width: 100,
  },
];
const beveragesAndSuppliesColumns: TableColumnsType<
  SiteMenuBeverageOrderView | SiteMenuSupplyOrderView
> = [
  {
    title: 'Beverage or Supply',
    // width: '60%',
    render: (_, rec) => {
      return (
        <Typography.Text>
          {'beverageName' in rec ? rec.beverageName : rec.supplyName}
        </Typography.Text>
      );
    },
    key: 'Beverage&Supply',
  },
  {
    title: 'Quantity',
    dataIndex: 'quantity',
    key: 'quantity',
    width: 100,
  },
];
const CustomerOrderView = (props: OrderViewProps): ReactNode => {
  const notification = useNotification();
  const { customerOrder, revisionNumber, onCustomerOrdersChange, onDismiss } = props;
  const { token } = theme.useToken();
  const { session } = useContext(AuthContext);
  const [isUpdatingState, setIsUpdatingState] = useState(false);
  const [selectedState, setSelectedState] = useState<OrderStatus | undefined>(
    OrderStatus.Submitted
  );
  const isArchived = useMemo(() => customerOrder instanceof ArchivedCustomerOrder, [customerOrder]);
  const siteMenuOrders = useOrderSiteMenuOrders(customerOrder.id, revisionNumber, isArchived);
  const securedApiV2 = useSecuredApiV2();
  /**
   * Converts siteMenuOrder[] to siteMenuOrderView[] to get only required data and
   * break circular references
   */
  const preprocessSiteMenuOrders = (siteMenuOrders: SiteMenuOrder[]): SiteMenuOrderView[] => {
    return siteMenuOrders.map((smo) => toSiteMenuOrderView(smo));
  };

  /**
   * Get SiteMenuOrders grouped by site and ordered by site.county then site.name
   */
  const sitesOrders: SiteMenuOrderView[][] = useMemo(() => {
    // Convert siteMenuOrder[] to SiteMenuOrderView[], to break circular reference and get only required data.
    const cleanSMOs = preprocessSiteMenuOrders(siteMenuOrders.data);
    // Sort site menu orders by site.county then site.name.
    const sortedSMOs = sortSiteMenuOrdersByCountyAndName(cleanSMOs) as SiteMenuOrderView[];

    // Group sorted site menu orders by site name
    const siteMenuOrdersBySite: Record<string, SiteMenuOrderView[]> = {};
    sortedSMOs.forEach(
      (smo) =>
        (siteMenuOrdersBySite[smo.site?.name ?? ''] = (
          siteMenuOrdersBySite[smo.site?.name ?? ''] ?? []
        ).concat(smo))
    );

    // Sort site menu orders of each site by route then meal category sortOrder
    Object.keys(siteMenuOrdersBySite).forEach(
      (key) =>
        (siteMenuOrdersBySite[key] = sortSiteMenuOrdersByRouteThenMealCategorySortOrder(
          siteMenuOrdersBySite[key]
        ) as SiteMenuOrderView[])
    );

    return Object.values(siteMenuOrdersBySite);
  }, [siteMenuOrders.data, isArchived]);

  useEffect(() => {
    siteMenuOrders.errorMessage &&
      notification.error({
        message: ' ',
        description: siteMenuOrders.errorMessage,
      });
  }, [siteMenuOrders.errorMessage]);
  useEffect(() => {
    setSelectedState(customerOrder.status);
  }, [customerOrder.status]);

  const updateState = (value: string): void => {
    setIsUpdatingState(true);
    securedApiV2
      .patch(
        `${CUSTOMER_ORDER_END_POINT_URL}/${customerOrder.id}/status`,
        { status: value },
        {},
        {}
      )
      .pipe(
        switchMap((response) =>
          response.data
            ? of(response.data.status as OrderStatus)
            : throwError(() => new ProblemDetailsDto(response))
        ),
        tap((status) => {
          notification.success({
            message: ' ',
            description: 'Updated successfully',
          });
          setSelectedState(status);
          onCustomerOrdersChange();
        }),
        catchError((error: ProblemDetailsDto) => {
          notification.error({
            description: error.detail ?? 'Something went wrong',
            message: ' ',
          });
          return EMPTY;
        }),
        finalize(() => {
          setIsUpdatingState(false);
        })
      )
      .subscribe();
  };
  return (
    <Flex gap="middle" vertical>
      <Card
        style={{ marginBlockStart: token.marginMD }}
        title={
          <Flex style={{ paddingBlock: token.paddingMD }} justify="space-between">
            <Flex justify="flex-start" vertical>
              <Typography.Title level={4} style={{ margin: 0 }}>
                Order #{customerOrder.id}
              </Typography.Title>
              <Typography.Text style={{ fontWeight: 400 }} disabled>
                {customerOrder.orderDate.format('dddd, LL')}
              </Typography.Text>
              <Typography.Title level={5} style={{ margin: 0 }}>
                {
                  orderStatuses.find((orderStatus) => orderStatus.value === customerOrder.status)
                    ?.label
                }
              </Typography.Title>
            </Flex>
            <Flex justify="flex-start" vertical gap="small">
              <Flex style={{ flexDirection: 'row-reverse', alignItems: 'center' }} gap="small">
                <Store color={token.colorTextDisabled} />
                <Typography.Text style={{ fontWeight: 400 }} disabled>
                  {customerOrder.customer.name}
                </Typography.Text>
              </Flex>
              <SyncSuspense condition={!isArchived}>
                <Flex style={{ flexDirection: 'row-reverse', alignItems: 'center' }} gap="small">
                  {session?.claims.role === Roles.ROLE_HHC_ADMIN && (
                    <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
                      {customerOrder.meta?.canUpdate && (
                        <Select
                          showSearch
                          style={{ width: 250 }}
                          value={selectedState}
                          placeholder="Order status"
                          optionFilterProp="label"
                          options={orderStatuses}
                          disabled={isUpdatingState}
                          loading={isUpdatingState}
                          onChange={(value) => {
                            if (
                              (value === OrderStatus.CanceledByCustomer ||
                                value === OrderStatus.CanceledByAdmin) &&
                              !customerOrder.meta?.canCancel
                            ) {
                              notification.error({
                                message: ' ',
                                description: 'This order cannot be canceled.',
                              });
                              return;
                            }
                            Modal.confirm({
                              width: '50%',
                              title: 'Confirm update state',
                              content: (
                                <Typography.Text
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.2rem',
                                  }}
                                >
                                  Are you sure you want to update the state of the order to{' '}
                                  {orderStatuses.find((os) => os.value === value)?.label}?
                                </Typography.Text>
                              ),
                              okText: 'Yes',
                              cancelText: 'No',
                              onOk: () => {
                                updateState(value);
                              },
                            });
                          }}
                        />
                      )}
                    </AuthorizedComponent>
                  )}
                </Flex>
              </SyncSuspense>
            </Flex>
          </Flex>
        }
        actions={[
          <Flex
            key="meta"
            style={{ paddingInline: 'var(--ant-padding-md)' }}
            vertical
            align="flex-start"
          >
            <Typography.Text style={{ fontSize: 'var(--ant-font-size-sm)' }} disabled>
              Last modified on{' '}
              {new LocalDate(
                customerOrder.customerOrderRevisions?.[
                  customerOrder.customerOrderRevisions.length - 1
                ]?.meta?.createdDate
              ).format('dddd, LL')}{' '}
              by{' '}
              {customerOrder.customerOrderRevisions?.[
                customerOrder.customerOrderRevisions.length - 1
              ]?.meta?.createdBy ?? 'system'}
            </Typography.Text>
            <Typography.Text style={{ fontSize: 'var(--ant-font-size-sm)' }} disabled>
              Created on {new LocalDate(customerOrder.meta?.createdDate).format('dddd, LL')} by{' '}
              {customerOrder.meta?.createdBy ?? 'system'}
            </Typography.Text>
          </Flex>,
        ]}
      >
        <SyncSuspense condition={!siteMenuOrders.isLoading} fallback={<LoadingSpin />}>
          {sitesOrders && (
            <>
              <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN, Roles.ROLE_CUSTOMER_ADMIN]}>
                <Table
                  size="small"
                  bordered={false}
                  dataSource={[]}
                  pagination={false}
                  columns={mealsListColumns.concat(pricesColumns)}
                  components={{ body: { row: () => null } }}
                />
              </AuthorizedComponent>
              <AuthorizedComponent roles={[Roles.ROLE_CUSTOMER_STANDARD]}>
                <Table
                  size="small"
                  bordered={false}
                  dataSource={[]}
                  pagination={false}
                  columns={mealsListColumns}
                  components={{ body: { row: () => null } }}
                />
              </AuthorizedComponent>
            </>
          )}

          {sitesOrders?.map((siteMenuOrders, idx) => {
            return (
              <Flex key={'' + idx} gap={token.marginSM} vertical>
                <Typography.Title style={{ margin: 0 }} level={5}>
                  <DetailedSite site={siteMenuOrders[0].site} />
                  {/* {`${site[0].site?.name} (HHC-${site[0].site?.siteNumber} / C-${site[0].site?.custSiteNumber} )`} */}
                </Typography.Title>
                <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN, Roles.ROLE_CUSTOMER_ADMIN]}>
                  <Table
                    size="small"
                    showHeader={false}
                    bordered={false}
                    dataSource={withRouteSubtotals(siteMenuOrders, routeSubtotalAmounts)}
                    pagination={false}
                    columns={withRouteSubtotalCells(
                      mealsListColumns.concat(pricesColumns),
                      MEALS_LABEL_COLUMN_INDEX,
                      routeSubtotalCells
                    )}
                    rowKey={(row) =>
                      isRouteSubtotal(row) ? (row as unknown as RouteSubtotalRow).rowKey : row.id!
                    }
                    rowClassName={routeSubtotalRowClassName}
                    expandable={{
                      expandedRowRender: (record) => (
                        <>
                          <Typography.Text
                            style={{ color: token.colorWarning }}
                            color={token.colorWarning}
                          >
                            {record.comment}
                          </Typography.Text>
                          {(record.siteMenuBeverageOrders ?? []).concat(
                            record.siteMenuSupplyOrders ?? ([] as any[])
                          ).length > 0 && (
                            <Table
                              style={{ marginInlineStart: token.marginXXL }}
                              columns={beveragesAndSuppliesColumns.concat(pricesColumns)}
                              dataSource={(record.siteMenuBeverageOrders ?? []).concat(
                                record.siteMenuSupplyOrders ?? ([] as any[])
                              )}
                              pagination={false}
                              size="small"
                              rowKey={(item) => 'Beverage&Supplies' + item.id!}
                            />
                          )}
                        </>
                      ),
                      rowExpandable: (record) =>
                        !isRouteSubtotal(record) &&
                        (!!record.comment ||
                          (record.siteMenuBeverageOrders ?? []).concat(
                            record.siteMenuSupplyOrders ?? ([] as any[])
                          ).length > 0),
                    }}
                  />
                </AuthorizedComponent>
                <AuthorizedComponent roles={[Roles.ROLE_CUSTOMER_STANDARD]}>
                  <Table
                    size="small"
                    showHeader={false}
                    bordered={false}
                    dataSource={withRouteSubtotals(siteMenuOrders, routeSubtotalAmounts)}
                    pagination={false}
                    columns={withRouteSubtotalCells(
                      mealsListColumns,
                      MEALS_LABEL_COLUMN_INDEX,
                      routeSubtotalCells
                    )}
                    rowKey={(row) =>
                      isRouteSubtotal(row) ? (row as unknown as RouteSubtotalRow).rowKey : row.id!
                    }
                    rowClassName={routeSubtotalRowClassName}
                    expandable={{
                      expandedRowRender: (record) => (
                        <>
                          <Typography.Text
                            style={{ color: token.colorWarning }}
                            color={token.colorWarning}
                          >
                            {record.comment}
                          </Typography.Text>
                          {(record.siteMenuBeverageOrders ?? []).concat(
                            record.siteMenuSupplyOrders ?? ([] as any[])
                          ).length > 0 && (
                            <Table
                              style={{ marginInlineStart: token.marginXXL }}
                              columns={beveragesAndSuppliesColumns}
                              dataSource={(record.siteMenuBeverageOrders ?? []).concat(
                                record.siteMenuSupplyOrders ?? ([] as any[])
                              )}
                              pagination={false}
                              size="small"
                            />
                          )}
                        </>
                      ),
                      rowExpandable: (record) =>
                        !!record.comment ||
                        (record.siteMenuBeverageOrders ?? []).concat(
                          record.siteMenuSupplyOrders ?? ([] as any[])
                        ).length > 0,
                    }}
                  />
                </AuthorizedComponent>
              </Flex>
            );
          })}
          {sitesOrders && (
            <Flex gap={token.marginXXS} wrap="wrap" style={{ marginBlockStart: token.marginSM }}>
              <Flex justify="space-between" flex="0 0 100%">
                <Typography.Title
                  level={3}
                  style={{ marginBlockStart: 0, marginBlockEnd: token.marginSM }}
                >
                  Order summary
                </Typography.Title>
              </Flex>
              <Flex justify="space-between" flex="0 0 100%">
                <Typography.Text>Total number of meals</Typography.Text>
                <Typography.Text>
                  {utils.numberFormat(totalMeals(sitesOrders.flat()))}
                </Typography.Text>
              </Flex>
              <Flex justify="space-between" flex="0 0 100%">
                <Typography.Text>Total number of units</Typography.Text>
                <Typography.Text>
                  {utils.numberFormat(
                    sitesOrders.reduce(
                      (sum, site) =>
                        sum +
                        site.reduce(
                          (total, smo) =>
                            total +
                            (smo.scheduledMeal.mealCategory.isCompleteMeal
                              ? smo.scheduledMeal.mealCategory.units
                              : 1) *
                              smo.quantity,
                          0
                        ),
                      0
                    )
                  )}
                </Typography.Text>
              </Flex>
              <Flex justify="space-between" flex="0 0 100%">
                <Typography.Text>Total number of Beverages</Typography.Text>
                <Typography.Text>
                  {utils.numberFormat(
                    sitesOrders.reduce(
                      (sum, site) =>
                        sum +
                        site.reduce(
                          (sum, smo) =>
                            sum +
                            (smo.siteMenuBeverageOrders ?? [])
                              .filter((bev) => !bev.isExtraBeverage)
                              .reduce((sum, bev) => sum + bev.quantity, 0),
                          0
                        ),
                      0
                    )
                  )}
                </Typography.Text>
              </Flex>
              <Flex justify="space-between" flex="0 0 100%">
                <Typography.Text>Total number of Extra Beverages</Typography.Text>
                <Typography.Text>
                  {utils.numberFormat(
                    sitesOrders.reduce(
                      (sum, site) =>
                        sum +
                        site.reduce(
                          (sum, smo) =>
                            sum +
                            (smo.siteMenuBeverageOrders ?? [])
                              .filter((bev) => bev.isExtraBeverage)
                              .reduce((sum, bev) => sum + bev.quantity, 0),
                          0
                        ),
                      0
                    )
                  )}
                </Typography.Text>
              </Flex>
              <Flex justify="space-between" flex="0 0 100%">
                <Typography.Text>Total number of Extra Supplies</Typography.Text>
                <Typography.Text>
                  {utils.numberFormat(
                    sitesOrders.reduce(
                      (sum, site) =>
                        sum +
                        site.reduce(
                          (sum, smo) =>
                            sum +
                            (smo.siteMenuSupplyOrders ?? []).reduce(
                              (sum, sup) => sum + sup.quantity,
                              0
                            ),
                          0
                        ),
                      0
                    )
                  )}
                </Typography.Text>
              </Flex>
              <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN, Roles.ROLE_CUSTOMER_ADMIN]}>
                <Flex justify="space-between" flex="0 0 100%">
                  <Typography.Text strong style={{ fontSize: token.fontSizeHeading4 }}>
                    Total Price
                  </Typography.Text>
                  <Typography.Text strong style={{ fontSize: token.fontSizeHeading5 }}>
                    {utils.currencyFormat(
                      sitesOrders.reduce(
                        (sum, site) =>
                          sum +
                          site.reduce((price, smo) => price + smo.unitPrice * smo.quantity, 0),
                        0
                      ) +
                        sitesOrders.reduce(
                          (sum, site) =>
                            sum +
                            site.reduce(
                              (sum, smo) =>
                                sum +
                                (smo.siteMenuBeverageOrders ?? []).reduce(
                                  (sum, bev) => sum + bev.unitPrice * bev.quantity,
                                  0
                                ),
                              0
                            ),
                          0
                        ) +
                        sitesOrders.reduce(
                          (sum, site) =>
                            sum +
                            site.reduce(
                              (sum, smo) =>
                                sum +
                                (smo.siteMenuSupplyOrders ?? []).reduce(
                                  (sum, sup) => sum + sup.unitPrice * sup.quantity,
                                  0
                                ),
                              0
                            ),
                          0
                        )
                    )}
                  </Typography.Text>
                </Flex>
              </AuthorizedComponent>
            </Flex>
          )}
        </SyncSuspense>
      </Card>
    </Flex>
  );
};

export default CustomerOrderView;
